import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaLinkedinIn,
  FaPaperPlane,
  FaCheckCircle,
} from 'react-icons/fa';
import { CONTACT, CONTACT_INFO, SOCIAL_LINKS } from '../config/content';
import { sendEnquiry } from '../services/contactService';
import styles from './Contact.module.css';
import SEO from '../components/SEO';

const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  Facebook: <FaFacebookF />,
  Instagram: <FaInstagram />,
  Twitter: <FaTwitter />,
  LinkedIn: <FaLinkedinIn />,
};

const contactInfoItems = [
  {
    icon: <FaEnvelope />,
    label: 'Email',
    value: CONTACT_INFO.email,
    href: CONTACT_INFO.emailHref,
  },
  {
    icon: <FaPhoneAlt />,
    label: 'Phone',
    value: CONTACT_INFO.phone,
    href: CONTACT_INFO.phoneHref,
  },
  {
    icon: <FaMapMarkerAlt />,
    label: 'Location',
    value: CONTACT_INFO.location,
    href: undefined,
  },
].filter((item) => item.value); // a market with no phone line shows none

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Honeypot: hidden from people, filled in by bots. Kept out of formData so a
  // person's reset never touches it.
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setFeedback('');
    try {
      const result = await sendEnquiry({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: formData.subject,
        message: formData.message,
        website,
      });
      setStatus('sent');
      setFeedback(result.message);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setFeedback((err as Error).message);
    }
  };

  return (
    <main className={styles.page}>
      <SEO path="/contact" />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <div className={styles.heroContent}>
          <motion.span
            className={styles.heroLabel}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            {CONTACT.hero.label}
          </motion.span>
          <motion.h1
            className={styles.heroTitle}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
          >
            {CONTACT.hero.title}
          </motion.h1>
          <motion.p
            className={styles.heroSubtitle}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
          >
            {CONTACT.hero.subtitle}
          </motion.p>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* ── Contact Split ── */}
      <section className={styles.section}>
        <Reveal className={styles.container}>
          <div className={styles.splitLayout}>
            {/* ── Left: Info ── */}
            <div className={styles.infoSide}>
              <h2 className={styles.infoTitle}>{CONTACT.form.sectionTitle}</h2>
              <p className={styles.infoDesc}>{CONTACT.form.sectionDesc}</p>

              <div className={styles.contactCards}>
                {contactInfoItems.map((item, i) => (
                  <div className={styles.contactCard} key={i}>
                    <div className={styles.contactCardIcon}>{item.icon}</div>
                    <div className={styles.contactCardInfo}>
                      <span className={styles.contactCardLabel}>{item.label}</span>
                      {item.href ? (
                        <a href={item.href} className={styles.contactCardValue}>
                          {item.value}
                        </a>
                      ) : (
                        <span className={styles.contactCardValue}>{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.socialSection}>
                <span className={styles.socialLabel}>{CONTACT.form.socialLabel}</span>
                <div className={styles.socialLinks}>
                  {SOCIAL_LINKS.map((s, i) => (
                    <a
                      key={i}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      aria-label={s.label}
                    >
                      {SOCIAL_ICONS[s.label]}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right: Form ── */}
            <div className={styles.formSide}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="firstName" className={styles.formLabel}>
                      {CONTACT.form.fields.firstName.label}
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={CONTACT.form.fields.firstName.placeholder}
                      className={styles.formInput}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="lastName" className={styles.formLabel}>
                      {CONTACT.form.fields.lastName.label}
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder={CONTACT.form.fields.lastName.placeholder}
                      className={styles.formInput}
                      required
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="email" className={styles.formLabel}>
                      {CONTACT.form.fields.email.label}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={CONTACT.form.fields.email.placeholder}
                      className={styles.formInput}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="phone" className={styles.formLabel}>
                      {CONTACT.form.fields.phone.label}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={CONTACT.form.fields.phone.placeholder}
                      className={styles.formInput}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="subject" className={styles.formLabel}>
                    {CONTACT.form.fields.subject.label}
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={styles.formSelect}
                    required
                  >
                    <option value="" disabled>
                      {CONTACT.form.fields.subject.placeholder}
                    </option>
                    {CONTACT.form.fields.subject.options.map((opt, i) => (
                      <option key={i} value={opt.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-')}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="message" className={styles.formLabel}>
                    {CONTACT.form.fields.message.label}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={CONTACT.form.fields.message.placeholder}
                    className={styles.formTextarea}
                    rows={6}
                    minLength={10}
                    maxLength={5000}
                    required
                  />
                </div>

                <div className={styles.honeypot} aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                <button type="submit" className={styles.submitBtn} disabled={status === 'sending'}>
                  <FaPaperPlane /> {status === 'sending' ? CONTACT.form.sendingButton : CONTACT.form.submitButton}
                </button>

                <div aria-live="polite">
                  {status === 'sent' && (
                    <p className={`${styles.formStatus} ${styles.formStatusSuccess}`}>
                      <FaCheckCircle /> {feedback}
                    </p>
                  )}
                  {status === 'error' && (
                    <p className={`${styles.formStatus} ${styles.formStatusError}`} role="alert">
                      {feedback}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── Map (markets with an office to show) ── */}
      {CONTACT.map && (
      <div className={styles.mapSection}>
        <Reveal>
          <div className={styles.mapContainer}>
            <iframe
              title={CONTACT.map.title}
              src={`https://www.google.com/maps?q=${CONTACT.map.latitude},${CONTACT.map.longitude}&hl=en&z=15&output=embed`}
              className={styles.mapEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className={styles.mapInteractionBlock} aria-hidden="true" />
            <div className={styles.mapPin} aria-hidden="true">
              <FaMapMarkerAlt />
            </div>
            <div className={styles.mapLabel}>
              <FaMapMarkerAlt className={styles.mapLabelIcon} />
              <span>{CONTACT.map.label}</span>
            </div>
          </div>
        </Reveal>
      </div>
      )}
    </main>
  );
};

export default Contact;
